/** @jsxImportSource @emotion/react */
import { CloseOutlined } from '@ant-design/icons'
import { css } from '@emotion/react'
import colors from '@sportninja/common/constants/appColors'
import { Country } from '@sportninja/common/hooks/useCountries'
import {
  Registration,
  TeamOfficialType,
} from '@sportninja/common/types/Registration'
import { App, Modal } from 'antd'
import { Dispatch, SetStateAction, useCallback, useEffect } from 'react'
import Button from 'src/components/ts-components/Button'
import Text from 'src/components/ts-components/Text'
import { useModalScroll } from 'src/hooks/useModalScroll'
import { RegistrationTableDivision } from 'src/pages/Schedule/PlayerRegistration/helpers'
import AdminTeamRegisterForm from './AdminTeamRegisterForm'
import {
  AdminTeamRegisterContext,
  AdminTeamRegisterContextType,
  AdminTeamRegisterContextWrapper,
} from './context'
import { Currency } from '@sportninja/common/types'

const styles = css`
  .my-modal-body {
    max-height: calc(90vh - 110px); // Account for header and footer
    overflow-y: auto;
  }
  .my-modal-mask {
    backdrop-filter: blur(1px);
  }
  .my-modal-header {
    border-bottom: 1px dotted ${colors.FIERY_RED};
  }
  .my-modal-footer {
    color: ${colors.FIERY_RED};
    position: sticky;
    bottom: 0;
    background: ${colors.SECONDARY_300};
    padding: 16px;
    border-top: 1px solid ${colors.SOFT_STEEL};
  }
  .my-modal-content {
    border: 1px solid #333;
    display: flex;
    flex-direction: column;
    max-height: 90vh;
  }
`
const classNames = {
  body: styles['my-modal-body'],
  mask: styles['my-modal-mask'],
  header: styles['my-modal-header'],
  footer: styles['my-modal-footer'],
  content: styles['my-modal-content'],
}
const modalStyles = {
  header: {
    backgroundColor: colors.SECONDARY_300,
    paddingLeft: '16px',
    paddingRight: '16px',
    position: 'sticky' as const,
    top: 0,
    zIndex: 1,
  },
  body: {
    borderBottom: `1px solid ${colors.SOFT_STEEL}`,
    borderTop: `1px solid ${colors.SOFT_STEEL}`,
    backgroundColor: colors.SECONDARY_300,
    flex: 1,
    overflow: 'auto',
  },
  content: {
    backgroundColor: colors.SECONDARY_300,
    paddingTop: '8px',
    paddingBottom: '0',
    paddingLeft: '0',
    paddingRight: '0',
    display: 'flex',
    flexDirection: 'column' as const,
    maxHeight: '90vh',
  },
  wrapper: {
    zIndex: 10001,
  },
  mask: {
    // backdropFilter: 'blur(2px)',
    backgroundColor: 'rgba(6, 10, 16, 0.8)',
    zIndex: 10000,
  },
  footer: {
    padding: '0',
    backgroundColor: colors.SECONDARY_300,
  },
}

type AdminTeamRegisterModalProps = {
  isFormOpen: boolean
  setIsFormOpen: Dispatch<SetStateAction<boolean>>
  divisionId: string
  refresh?: () => void
  scheduleId: string
  registration: Registration
  teamOfficialTypes: TeamOfficialType[]
  cost: string
  deposit: string
  singlePayment: boolean
  costValue: number
  division: RegistrationTableDivision | null
  currency: Currency
  countries: Country[]
}

const antModalStyles = css`
  .ant-modal-wrap {
    z-index: 10001;
  }
  .ant-modal-content {
    overflow: hidden;
    max-height: 95vh;
    display: flex;
    flex-direction: column;
  }
  .ant-modal-body {
    flex: 1;
    overflow-y: auto;
    border-bottom: none !important;
  }
  .ant-modal-footer {
    position: sticky;
    bottom: 0;
    background: ${colors.SECONDARY_300};
    padding: 16px;
    border-top: 1px solid ${colors.SOFT_STEEL};
  }
`

const AdminTeamRegisterModal = ({
  isFormOpen,
  setIsFormOpen,
  divisionId,
  refresh,
  scheduleId,
  registration,
  teamOfficialTypes,
  cost,
  deposit,
  singlePayment,
  costValue,
  division,
  currency,
  countries,
}: AdminTeamRegisterModalProps) => {
  const { message } = App.useApp()
  const { openModal, closeModal } = useModalScroll()

  const getFooter = useCallback(
    (context: AdminTeamRegisterContextType) => {
      const { clearState, state, onSubmit } = context
      return (
        <div
          css={css`
            display: flex;
            justify-content: flex-end;
            align-items: center;
            gap: 16px;
            width: 33%;
            margin-left: auto;
            min-height: 60px;
            padding: 0 16px;
          `}
        >
          <Button
            text='Cancel'
            onClick={() => {
              clearState()
              setIsFormOpen(false)
            }}
            variant='secondary'
            disabled={state.loading}
          />

          <Button
            text='Save'
            onClick={onSubmit}
            variant='primary'
            isLoading={state.loading}
            disabled={
              state?.loading ||
              !state?.team ||
              !state?.team_officials ||
              state?.team_officials?.length === 0 ||
              !state?.team_officials?.some((official) => official?.is_treasurer)
            }
          />
        </div>
      )
    },
    [setIsFormOpen]
  )

  const closeIcon = useCallback(() => {
    return (
      <CloseOutlined
        css={css`
          color: ${colors.SOLITUDE};
          font-size: 16px;
        `}
      />
    )
  }, [])

  useEffect(() => {
    if (isFormOpen) {
      openModal()
    } else {
      closeModal()
    }
  }, [closeModal, isFormOpen, openModal])

  return (
    <AdminTeamRegisterContextWrapper
      messageApi={message}
      setIsFormOpen={setIsFormOpen}
      isOpen={isFormOpen}
      divisionId={divisionId}
      refresh={refresh}
      scheduleId={scheduleId}
      registration={registration}
      teamOfficialTypes={teamOfficialTypes}
      cost={cost}
      deposit={deposit}
      singlePayment={singlePayment}
      countries={countries}
      costValue={costValue}
      division={division}
      currency={currency}
    >
      <AdminTeamRegisterContext.Consumer>
        {(context) => {
          if (!context) {
            return null
          }

          return (
            <Modal
              width={1080}
              closeIcon={closeIcon()}
              title={
                <Text variant='heading3' weight='semiBold' color={colors.WHITE}>
                  New Team
                </Text>
              }
              footer={getFooter(context)}
              open={isFormOpen}
              onCancel={() => {
                context.clearState()
                setIsFormOpen(false)
              }}
              centered
              classNames={classNames}
              styles={{
                ...modalStyles,
                body: {
                  ...modalStyles.body,
                  overflowY: 'auto' as const,
                },
              }}
              maskClosable={false}
              css={antModalStyles}
            >
              <AdminTeamRegisterForm />
            </Modal>
          )
        }}
      </AdminTeamRegisterContext.Consumer>
    </AdminTeamRegisterContextWrapper>
  )
}

export default AdminTeamRegisterModal
